import React from 'react';
import {Box, Menu, MenuItem, Typography} from '@mui/material';

// SourceScore
// Props:
// - value: number | null (0..1). If null => "unverified"
// - onChange: (newValue: number|null) => void
// - docSourceType?: string (optional label to display instead of mark label)
// - size / sx optional styling props can be passed via rest

const MARKS = [
    {value: 4, label: 'Credible', color: 'rgba(33,186,69,1)', bgColor: 'rgba(33,186,69,0.5)'}, // blue
    {value: 3, label: 'Reliable', color: 'rgba(181,204,24,1)', bgColor: 'rgba(181,204,24,0.5)'}, // green
    {value: 2, label: 'Uncertain', color: 'rgba(251,189,8,1)', bgColor: 'rgba(251,189,8,0.5)'}, // yellow
    {value: 1, label: 'Unreliable', color: 'rgba(242,113,28,1)', bgColor: 'rgba(242,113,28,0.5)'}, // orange
    {value: 0, label: 'Disinformative', color: 'rgba(219,40,40,1)', bgColor: 'rgba(219,40,40,0.5)'}, // red
];


function indexToValue(index) {
    // map 0..4 -> 0..1
    return index / 4;
}

function valueToIndex(v) {
    if (v === null || typeof v === 'undefined' || Number.isNaN(v)) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    const clamped = Math.max(0, Math.min(1, n));
    return Math.round(clamped * 4);
}

export default function SourceScore({
                                        value = null, onChange = () => {
    }, docSourceType = undefined, sx, ...rest
                                    }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'source-score-menu' : undefined;

    const currentIndex = valueToIndex(value);
    const currentLabel = currentIndex === null ? 'Unverified' : (MARKS.find(m => m.value === currentIndex)?.label ?? 'unverified');

    const handleToggle = (e) => {
        setAnchorEl(prev => (prev ? null : e.currentTarget));
    };
    const handleClose = () => setAnchorEl(null);

    const handleSelectIndex = (index) => {
        onChange(indexToValue(index));
        handleClose();
    };

    const handleSelectUnverified = () => {
        onChange(null);
        handleClose();
    };

    // choose background for the control
    const bgColor = currentIndex === null ? '#EEEEEE' : (MARKS.find(m => m.value === currentIndex)?.bgColor ?? '#EEEEEE');
    const color = currentIndex === null ? '#000000' : (MARKS.find(m => m.value === currentIndex)?.color ?? '#000000');

    const labelText = docSourceType ? docSourceType : (currentLabel ?? 'OSINT');

    return (
        <Box component="span" sx={{display: 'inline-block', ...sx}} {...rest}>

            <Typography
                component="span"
                role="button"
                tabIndex={0}
                aria-controls={id}
                aria-haspopup="true"
                onClick={handleToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(e);
                    }
                }}
                variant={'body2'}
                color={'text.primary'}
                px={1}
                bgcolor={bgColor}
                sx={{
                    textTransform: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderRadius: '4px',
                    borderColor: color,
                }}
            >
                {labelText}
            </Typography>


            <Menu
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
            >

                {MARKS.map((mark, idx) => (
                    <MenuItem key={mark.value} selected={currentIndex === mark.value}
                              onClick={() => handleSelectIndex(mark.value)}
                    >
                        <Box
                            bgcolor={mark.color} sx={{textTransform: 'none'}} width={16} height={16}
                            borderRadius="4px"
                            marginRight={1}
                        />
                        <Typography variant="body1" sx={{textTransform: 'none', flexGrow: 1}}
                        >{mark.label}</Typography>
                    </MenuItem>
                ))}

                <MenuItem selected={currentIndex === null} onClick={handleSelectUnverified}>
                    <Typography variant="body1">Unverified</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
}
